import type { Coach, ApiResponse, Attendance, Lesson, Term, Booking, LessonOverride, CoachAvailability, CoachAvailabilityException, CoachAbsence, CoachHoursReportRow, CoachHoursSessionRow, CoachSportQualification } from '@court-sync/shared';
import type { CoachSportQualificationFormData } from '@court-sync/shared';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { apiGet, apiPost, apiPut, apiDelete, publicApiGet, apiClient } from './apiClient';

/** A coach profile enriched with academy display info (returned from GET /api/coaches/me) */
export interface CoachWithAcademy extends Coach {
  academyName?: string;
  academySlug?: string;
  academyLogoUrl?: string;
}

/** Coach schedule data returned from GET /api/coaches/me/schedule */
export interface CoachScheduleData {
  coach: Coach;
  lessons: Lesson[];
  terms: Record<string, Term>;
  bookings: Booking[];
  overrides: LessonOverride[];
  students: Record<string, string>;
  attendance: Record<string, Attendance[]>;
  makeupSessions?: Array<{ lessonId: string; date: string }>;
  makeupSessionReservations?: Array<{
    id: string;
    lessonId: string;
    sessionDate: string;
    studentId: string;
    customerId: string;
  }>;
  reportedAbsences?: Array<{ lessonId: string; date: string; bookingId: string }>;
  courts?: Record<string, string>;
}

/** Single attendance record to mark */
export interface AttendanceMark {
  bookingId?: string;
  makeupSessionReservationId?: string;
  studentId: string;
  status: 'scheduled' | 'attended' | 'missed' | 'cancelled';
  notes?: string;
}

export const coachService = {
  async getCoaches(
    academyId?: string,
    options?: { search?: string }
  ): Promise<ApiResponse<Coach[]>> {
    const params: Record<string, string> = {};
    if (academyId) params.academyId = academyId;
    if (options?.search?.trim()) params.search = options.search.trim();
    return apiGet<Coach[]>('/coaches', { params });
  },

  async getCoach(id: string): Promise<ApiResponse<Coach>> {
    return apiGet<Coach>(`/coaches/${id}`);
  },

  async createCoach(coachData: Omit<Coach, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>('/coaches', coachData);
  },

  async updateCoach(id: string, coachData: Partial<Omit<Coach, 'id' | 'createdAt' | 'updatedAt'>>): Promise<ApiResponse> {
    return apiPut(`/coaches/${id}`, coachData);
  },

  async deleteCoach(id: string): Promise<ApiResponse> {
    return apiDelete(`/coaches/${id}`);
  },

  async selfAdd(academyId: string, coachData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    bio?: string;
    specialties?: string[];
    hourlyRate?: number;
  }): Promise<ApiResponse<{ coach: Coach }>> {
    return apiPost<{ coach: Coach }>('/coaches/self-add', {
      academyId,
      ...coachData,
    });
  },

  async getPublicCoaches(academyId: string): Promise<Coach[]> {
    const response = await publicApiGet<Coach[]>(`/public/coaches/${academyId}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch coaches');
  },

  async getMyCoachProfiles(): Promise<CoachWithAcademy[]> {
    const response = await apiGet<CoachWithAcademy[]>('/coaches/me');
    if (response.success) {
      return response.data ?? [];
    }
    throw new Error(response.error ?? 'Failed to load coach profiles');
  },

  async getCoachSchedule(
    academyId: string,
    options?: { startDate?: string; endDate?: string }
  ): Promise<CoachScheduleData> {
    const response = await apiGet<CoachScheduleData>('/coaches/me/schedule', {
      params: { academyId, ...options },
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load coach schedule');
  },

  async getCoachLessonsPaginated(
    academyId: string,
    page: number,
    limit: number
  ): Promise<{ data: Lesson[]; terms: Record<string, Term>; pagination: PaginationState }> {
    const response = await apiClient.get<{
      success: boolean;
      data: Lesson[];
      terms?: Record<string, Term>;
      pagination: PaginationState;
      error?: string;
    }>('/coaches/me/lessons', {
      params: { academyId, page, limit },
    });
    const body = response.data;
    if (!body.success || !body.data) {
      throw new Error(body.error ?? 'Failed to load coach lessons');
    }
    return {
      data: body.data,
      terms: body.terms ?? {},
      pagination: body.pagination,
    };
  },

  async getCoachHoursWorked(params: {
    academyId: string;
    from: string;
    to: string;
    coachId?: string;
    lessonType?: 'group' | 'private' | 'camp';
    detailed?: boolean;
    billByAttendance?: boolean;
  }): Promise<{ aggregates: CoachHoursReportRow[]; detailed?: CoachHoursSessionRow[] }> {
    const response = await apiGet<{ aggregates: CoachHoursReportRow[]; detailed?: CoachHoursSessionRow[] }>(
      '/coaches/hours-worked',
      {
        params: {
          academyId: params.academyId,
          from: params.from,
          to: params.to,
          ...(params.coachId ? { coachId: params.coachId } : {}),
          ...(params.lessonType ? { lessonType: params.lessonType } : {}),
          ...(params.detailed ? { detailed: 'true' } : {}),
          ...(params.billByAttendance ? { billByAttendance: 'true' } : {}),
        },
      }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load coach hours');
  },

  async getStudentProgramIds(academyId: string, studentId: string): Promise<string[]> {
    const response = await apiGet<{ programIds: string[] }>(
      `/coaches/me/students/${encodeURIComponent(studentId)}/program-ids`,
      { params: { academyId } }
    );
    if (response.success && response.data?.programIds) {
      return response.data.programIds;
    }
    throw new Error(response.error ?? 'Failed to load student program ids');
  },

  async markAttendance(
    academyId: string,
    lessonId: string,
    sessionDate: string,
    records: AttendanceMark[],
    options?: { sessionId?: string }
  ): Promise<void> {
    const response = await apiPost('/coaches/attendance/mark', {
      academyId,
      lessonId,
      sessionDate,
      ...(options?.sessionId ? { sessionId: options.sessionId } : {}),
      records,
    });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to mark attendance');
    }
  },

  async updateMyProfile(
    academyId: string,
    coachId: string,
    data: Partial<Pick<Coach, 'bio' | 'specialties' | 'phone' | 'email'>>
  ): Promise<void> {
    const response = await apiPut('/coaches/me/profile', { academyId, coachId, ...data });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to update profile');
    }
  },

  async getInviteStatus(academyId: string): Promise<Record<string, 'pending' | 'accepted' | 'expired' | 'revoked'>> {
    const response = await apiGet<Record<string, 'pending' | 'accepted' | 'expired' | 'revoked'>>(
      '/coaches/invite-status',
      { params: { academyId } }
    );
    if (response.success) {
      return response.data ?? {};
    }
    throw new Error(response.error ?? 'Failed to fetch invite status');
  },

  async sendInvite(
    academyId: string,
    email: string,
    coachId?: string
  ): Promise<{ inviteId: string }> {
    const response = await apiPost<{ inviteId: string }>('/coaches/invite', { academyId, email, coachId });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to send invite');
  },

  async getInviteDetails(token: string): Promise<{
    academyName: string;
    academyLogoUrl?: string;
    coachName: string;
    email: string;
    status: 'pending' | 'accepted' | 'expired';
  }> {
    const response = await publicApiGet<{
      academyName: string;
      academyLogoUrl?: string;
      coachName: string;
      email: string;
      status: 'pending' | 'accepted' | 'expired';
    }>(`/coaches/invite/${token}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Invalid invite link');
  },

  async acceptInvite(token: string): Promise<{ academySlug: string }> {
    const response = await apiPost<{ academySlug: string }>(`/coaches/invite/${token}/accept`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to accept invitation');
  },

  async getAvailability(coachId: string, academyId: string): Promise<CoachAvailability[]> {
    const response = await apiGet<CoachAvailability[]>(`/coaches/${coachId}/availability`, { params: { academyId } });
    if (response.success) {
      return response.data ?? [];
    }
    throw new Error(response.error ?? 'Failed to fetch availability');
  },

  async updateAvailability(
    coachId: string,
    academyId: string,
    slots: Array<{ dayOfWeek: number; startTime: string; endTime: string; isAvailable: boolean }>
  ): Promise<void> {
    const response = await apiPut(`/coaches/${coachId}/availability`, { academyId, slots });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to update availability');
    }
  },

  async getAvailabilityExceptions(
    coachId: string,
    academyId: string,
    from?: string,
    to?: string
  ): Promise<CoachAvailabilityException[]> {
    const params: Record<string, string> = { academyId };
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await apiGet<CoachAvailabilityException[]>(
      `/coaches/${coachId}/availability-exceptions`,
      { params }
    );
    if (response.success) {
      return response.data ?? [];
    }
    throw new Error(response.error ?? 'Failed to fetch exceptions');
  },

  async addAvailabilityException(
    coachId: string,
    data: {
      academyId: string;
      dateFrom: string;
      dateTo?: string;
      isAvailable: boolean;
      startTime?: string;
      endTime?: string;
      reason?: string;
    }
  ): Promise<{ id: string }> {
    const response = await apiPost<{ id: string }>(`/coaches/${coachId}/availability-exceptions`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to add exception');
  },

  async deleteAvailabilityException(coachId: string, exceptionId: string): Promise<void> {
    const response = await apiDelete(`/coaches/${coachId}/availability-exceptions/${exceptionId}`);
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to delete exception');
    }
  },

  async reportAbsence(data: {
    lessonId: string;
    originalDate: string;
    reason?: string;
  }): Promise<{ absenceId: string }> {
    const response = await apiPost<{ absenceId: string }>('/coach-absences', data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to report absence');
  },

  async getPendingAbsences(academyId: string): Promise<CoachAbsence[]> {
    const response = await apiGet<CoachAbsence[]>('/coach-absences', { params: { academyId, status: 'pending' } });
    if (response.success) {
      return response.data ?? [];
    }
    throw new Error(response.error ?? 'Failed to fetch absences');
  },

  async reassignCoach(absenceId: string, replacementCoachId: string): Promise<void> {
    const response = await apiPost(`/coach-absences/${absenceId}/reassign`, { replacementCoachId });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to reassign coach');
    }
  },

  async cancelLessonForAbsence(absenceId: string, reason?: string): Promise<void> {
    const response = await apiPost(`/coach-absences/${absenceId}/cancel-lesson`, { reason });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to cancel lesson');
    }
  },

  async getCoachStats(
    coachId: string,
    academyId: string
  ): Promise<{
    attendanceRate?: number;
    totalAssigned?: number;
    totalCoached?: number;
    reportedAbsences?: number;
  }> {
    const response = await apiGet<{
      attendanceRate?: number;
      totalAssigned?: number;
      totalCoached?: number;
      reportedAbsences?: number;
    }>(`/coaches/${coachId}/stats`, {
      params: { academyId },
    });
    if (response.success) {
      return response.data ?? {};
    }
    throw new Error(response.error ?? 'Failed to fetch coach stats');
  },

  async getSessionCoachAttendance(params: {
    academyId: string;
    lessonId: string;
    sessionDate: string;
    sessionId?: string;
  }) {
    const response = await apiGet<import('@court-sync/shared').CoachSessionAttendanceRosterEntry[]>(
      '/coaches/session-attendance',
      { params }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load coach attendance');
  },

  async markSessionCoachAttendance(body: {
    academyId: string;
    lessonId: string;
    sessionDate: string;
    sessionId?: string;
    records: Array<{ coachId: string; status: string; notes?: string }>;
  }): Promise<void> {
    const response = await apiPost('/coaches/session-attendance', body);
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to mark coach attendance');
    }
  },

  async getMyAttendanceSummary(
    academyId: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      statuses?: import('@court-sync/shared').CoachAttendanceStatus[] | 'all';
    }
  ) {
    const statusesParam =
      options?.statuses === 'all'
        ? 'all'
        : options?.statuses?.length
          ? options.statuses.join(',')
          : undefined;

    const response = await apiGet<{
      summary: import('@court-sync/shared').CoachAttendanceSummary;
      sessions: import('@court-sync/shared').CoachAttendanceSessionRow[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>('/coaches/me/attendance-summary', {
      params: {
        academyId,
        ...(options?.page != null ? { page: options.page } : {}),
        ...(options?.limit != null ? { limit: options.limit } : {}),
        ...(options?.search?.trim() ? { search: options.search.trim() } : {}),
        ...(statusesParam ? { statuses: statusesParam } : {}),
      },
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load attendance summary');
  },

  async getMySessionAttendanceStatus(params: {
    academyId: string;
    lessonId: string;
    sessionDate: string;
    sessionId?: string;
  }) {
    const response = await apiGet<import('@court-sync/shared').CoachSessionSelfAttendanceStatus>(
      '/coaches/me/session-attendance/status',
      { params }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load attendance status');
  },

  async checkInSelfSession(body: {
    academyId: string;
    lessonId: string;
    sessionDate: string;
    sessionId?: string;
    status: 'attended' | 'scheduled';
  }): Promise<void> {
    const response = await apiPost('/coaches/me/session-attendance', body);
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to update check-in');
    }
  },

  async getCoachSportQualifications(
    coachId: string,
    academyId: string
  ): Promise<CoachSportQualification[]> {
    const response = await apiGet<CoachSportQualification[]>(
      `/coaches/${coachId}/sport-qualifications`,
      { params: { academyId } }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to load sport qualifications');
  },

  async updateCoachSportQualifications(
    coachId: string,
    academyId: string,
    qualifications: CoachSportQualificationFormData[]
  ): Promise<ApiResponse> {
    return apiPut(`/coaches/${coachId}/sport-qualifications`, {
      academyId,
      qualifications,
    });
  },
};
